'use client';

import type { GroupColumnDef } from '@tanstack/react-table';
import { calcMaxSize, calcMinSize } from '@/components/Table/utils/calcColumnSize';
import type { AvailableSeries } from '@/types/series';
import type { SeriesDefinition } from '@/types/statisticsSeriesDefs';
import { ColumnCell } from './ColumnCell';
import { ColumnHeader } from './ColumnHeader';
import { titleColumn } from './TitleColumn';

const createDeltaSeries = (series: SeriesDefinition): SeriesDefinition => ({
  ...series,
  slug: `${series.slug}:delta` as AvailableSeries,
  shortname: series.delta === 'relative' ? '%' : 'Δ',
  unit: series.delta === 'relative' ? '%' : 'Δ',
});

import type {
  CellValue,
  DynamicTable,
  DynamicTableRow,
  LinkCellValue,
  LinkItem,
  NumericCellValue,
  Period,
  TitleColumn,
} from '@/components/Table/types';

export type {
  CellValue,
  DynamicTable,
  DynamicTableRow,
  LinkCellValue,
  LinkItem,
  NumericCellValue,
  Period,
  TitleColumn,
};

export const createDynamicColumns = (
  series: SeriesDefinition[],
  data: DynamicTable | undefined,
  hasDelta: boolean = false,
) => {
  const rows = data?.rows;
  const periods = data?.periods;
  const extendedSeries = series.map((s) => (hasDelta ? [s, createDeltaSeries(s)] : [s]));

  return [
    titleColumn(rows),
    ...extendedSeries.map((groupedSeries): GroupColumnDef<DynamicTableRow> => {
      const series = groupedSeries[0];
      return {
        id: series.slug,
        meta: {
          title: series.title,
          description: series.description,
          draggableId: series.slug,
          isDraggable: true,
          isVisible: false,
        },
        columns: groupedSeries.map((subSeries) => {
          const displayUnit = subSeries.isLink || subSeries.slug.includes(':delta') ? undefined : subSeries.unit;
          return {
            id: `_${subSeries.slug}`,
            accessorKey: subSeries.slug,
            meta: {
              draggableId: series.slug,
              isDraggable: true,
            },
            minSize: rows
              ? calcMinSize(subSeries.slug, rows, subSeries.shortname, hasDelta, displayUnit)
              : hasDelta
                ? 100
                : 170,
            maxSize: rows
              ? calcMaxSize(subSeries.slug, rows, subSeries.shortname, hasDelta, displayUnit)
              : hasDelta
                ? 100
                : 170,
            header: (ctx) => (
              <ColumnHeader
                data={rows}
                periods={periods}
                series={subSeries}
                header={ctx.header}
              />
            ),
            cell: (row) => (
              <ColumnCell
                data={rows}
                series={subSeries}
                row={row}
              />
            ),
          };
        }),
      };
    }),
  ];
};
